import { useCallback } from 'react';

import { useRegionalConfiguration } from '@/entities/regional-configuration/hooks/useRegionalConfiguration';
import useFullUser from '@/hooks/providers/useFullUser';
import useRegion from '@/hooks/providers/useRegion';
import { getDomain, MfeNames } from '@/utils/domains';
import { getMarketingRegionIfEnabled } from '@/utils/server/region-utils';

type GetRouteWithRegionParams = {
    includeRegionForNonSubscriber: boolean;
    alwaysIncludeRegion?: boolean;
    isSubscriber: boolean;
    baseUrl: string;
    region: string;
    duplicateRegionSegments?: string[];
};

/** True for `http(s):` and protocol-relative `//` URLs — these must not be joined with `baseUrl`. */
function isAbsoluteOrProtocolRelativeUrl(href: string): boolean {
    const h = href.trim();
    return h.startsWith('http://') || h.startsWith('https://') || h.startsWith('//');
}

/**
 * True for same-page fragment links (`#`, `#section`). These are not routes — the OneTrust CMP
 * footer link uses `href: '#'` — so they must be left untouched. Region-prefixing or
 * canonicalizing a hash turns the no-op anchor into a `/#` / `/us/#` root-route navigation and
 * prefetch target.
 */
function isHashOnlyHref(href: string): boolean {
    return href.trim().startsWith('#');
}

/**
 * Drops leading region segments from `path` (href without its optional leading `/`) so we do not
 * produce duplicate prefixes when callers pass paths that already include either the target region
 * or the raw country region.
 */
function pathWithoutDuplicateLeadingRegion(path: string, regionSegments: string[]): string {
    let next = path;
    const uniqueRegionSegments = Array.from(new Set(regionSegments.filter(Boolean)));

    while (next) {
        const duplicateSegment = uniqueRegionSegments.find(
            (segment) =>
                next.toLowerCase() === segment || next.toLowerCase().startsWith(`${segment}/`),
        );

        if (!duplicateSegment) {
            break;
        }

        if (next.toLowerCase() === duplicateSegment.toLowerCase()) {
            return '';
        }

        next = next.slice(duplicateSegment.length + 1);
    }

    return next;
}

export const getRouteWithRegion =
    ({
        includeRegionForNonSubscriber,
        alwaysIncludeRegion,
        isSubscriber,
        baseUrl,
        region,
        duplicateRegionSegments = [region],
    }: GetRouteWithRegionParams) =>
    (href: string, getFullPath = false): string => {
        if (isAbsoluteOrProtocolRelativeUrl(href) || isHashOnlyHref(href)) {
            return href.trim();
        }

        // Truth table:
        //   - Pluto TV (alwaysIncludeRegion=true): always include region.
        //   - Paramount+ subscriber: do not include region.
        //   - Paramount+ non-subscriber: include region only when includeRegionForNonSubscriber is true.
        let shouldIncludeRegion: boolean;

        if (alwaysIncludeRegion) {
            shouldIncludeRegion = true;
        } else if (isSubscriber === false) {
            shouldIncludeRegion = includeRegionForNonSubscriber;
        } else {
            shouldIncludeRegion = false;
        }

        // Don't add region
        if (!shouldIncludeRegion) {
            if (getFullPath) {
                const path = href.startsWith('/') ? href.slice(1) : href;
                return `${baseUrl}/${path}`;
            }
            return href;
        }

        const rawPath = href.startsWith('/') ? href.slice(1) : href;
        const path = pathWithoutDuplicateLeadingRegion(rawPath, duplicateRegionSegments);

        if (getFullPath) {
            return baseUrl + `/${region}/${path}`;
        }

        // Only domestic `us` (and P+, which never sets alwaysIncludeRegion) serves its front door
        // at the region-less `/`; Pluto's international regions serve it at `/{region}/`.
        if (path === '' && (region === 'us' || !alwaysIncludeRegion)) {
            return '/';
        }

        return `/${region}/${path}`;
    };

/**
 * Inverse of {@link getRouteWithRegion}: strips the leading region segment(s) from a (possibly
 * region-prefixed) href to recover the canonical Next.js page route (e.g. `/us/movies/` -> `/movies/`).
 *
 * Used as the `<Link href>` value so the Next.js route-loader keys / dedups / prefetches on the real
 * page route, while the region URL is passed as `<Link as>`. This fixes route chunks being fetched
 * twice without changing the browser URL or the server rewrite behavior.
 */
export const getCanonicalRoute =
    ({ duplicateRegionSegments = [] }: { duplicateRegionSegments?: string[] }) =>
    (href: string): string => {
        if (isAbsoluteOrProtocolRelativeUrl(href) || isHashOnlyHref(href)) {
            return href.trim();
        }

        const trimmed = href.trim();
        // Preserve an empty href instead of fabricating a `/` home route — mirrors
        // getRouteWithRegion(''), which is a no-op. Callers pass '' to mean "no link".
        if (!trimmed) {
            return href;
        }

        const rawPath = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
        const canonicalPath = pathWithoutDuplicateLeadingRegion(rawPath, duplicateRegionSegments);

        return `/${canonicalPath}`;
    };

const useRegionRoute = (appName?: MfeNames) => {
    const app = appName ? appName : (process.env.NEXT_PUBLIC_APP_NAME as MfeNames);
    const { region } = useRegion();
    const { isSubscriber } = useFullUser();
    const { getConfigByKey } = useRegionalConfiguration({
        appName: app,
        region,
    });

    const baseUrl = getDomain();
    const includeRegionForNonSubscriber = !!getConfigByKey('options.includeRegionForNonSubscriber');
    const alwaysIncludeRegion = !!getConfigByKey('options.alwaysIncludeRegion');
    const allowMarketingRegionInPath = !!getConfigByKey('options.allowMarketingRegionInPath');

    // When enabled (e.g. Pluto TV), route segments should use the marketing region
    // (e.g. `latam` for MX/AR, `gsa` for DE/AT/CH, `gb_ie` for GB/IE) rather than the raw
    // country code. Fall back to the raw region when no mapping exists.
    const regionInPath = getMarketingRegionIfEnabled(allowMarketingRegionInPath, region);

    const _getRouteWithRegion = useCallback(
        () =>
            getRouteWithRegion({
                includeRegionForNonSubscriber,
                alwaysIncludeRegion,
                isSubscriber,
                baseUrl,
                region: regionInPath,
                duplicateRegionSegments: [regionInPath, region],
            }),
        [
            includeRegionForNonSubscriber,
            alwaysIncludeRegion,
            isSubscriber,
            baseUrl,
            regionInPath,
            region,
        ],
    );

    const __getRouteWithRegion = useCallback(
        (href: string, getFullPath = false): string => _getRouteWithRegion()(href, getFullPath),
        [_getRouteWithRegion],
    );

    const _getCanonicalRoute = useCallback(
        () => getCanonicalRoute({ duplicateRegionSegments: [regionInPath, region] }),
        [regionInPath, region],
    );

    const __getCanonicalRoute = useCallback(
        (href: string): string => _getCanonicalRoute()(href),
        [_getCanonicalRoute],
    );

    return {
        getRouteWithRegion: __getRouteWithRegion,
        getCanonicalRoute: __getCanonicalRoute,
    };
};

export default useRegionRoute;
